import { useMemo, useState } from 'react';
import { fmtNum, fmtTRY } from '../../yeni-design/primitives';
import { KDV_KAR_NOTU, oranMetni } from '../bicim';
import { sayi, type Sayisal } from '../ozetHesaplari';
import { useSaSorgu } from '../useSatisVerisi';
import type { DetayGovdeProps } from './detayTipleri';
import { AramaKutusu, aramaSuz, DetayMesaj, DetayYukleniyor } from './ortak';

/** 990056: donemde satan butun urunler (LIMIT yok). Miktar kolonu raporda yok. */
interface UrunSatiri {
  stok_kodu: string;
  urun_adi: string;
  kategori: string;
  ciro: Sayisal;
  maliyet: Sayisal;
  kar: Sayisal;
}

export interface TumUrunSatiri {
  stokKodu: string;
  urunAdi: string;
  kategori: string;
  ciro: number;
  maliyet: number;
  kar: number;
  /** Ekranin TEK marj tanimi: kar / urun cirosu (F-2). Ciro 0 ise null. */
  marj: number | null;
}

export type SiraAlani = 'urunAdi' | 'kategori' | 'ciro' | 'maliyet' | 'kar' | 'marj';
export interface Siralama {
  alan: SiraAlani;
  azalan: boolean;
}

/** Bir kerede cizilen satir: binlerce satirlik DOM modali agirlastirirdi. */
export const SAYFA_BOYU = 100;

export function urunSatirlari(rows: UrunSatiri[]): TumUrunSatiri[] {
  return rows.map((r) => {
    const ciro = sayi(r.ciro);
    const kar = sayi(r.kar);
    return {
      stokKodu: String(r.stok_kodu),
      urunAdi: String(r.urun_adi),
      kategori: String(r.kategori),
      ciro,
      maliyet: sayi(r.maliyet),
      kar,
      marj: ciro !== 0 ? (kar / ciro) * 100 : null,
    };
  });
}

/** Marji olmayan (cirosu 0) urun yon ne olursa olsun sona. Esitlikte urun adi: sira her acilista ayni kalir. */
export function urunSirala(rows: TumUrunSatiri[], { alan, azalan }: Siralama): TumUrunSatiri[] {
  const yon = azalan ? -1 : 1;
  return [...rows].sort((a, b) => {
    const x = a[alan];
    const y = b[alan];
    if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
    const fark = typeof x === 'string' ? x.localeCompare(String(y), 'tr') : x - Number(y);
    return fark !== 0 ? fark * yon : a.urunAdi.localeCompare(b.urunAdi, 'tr');
  });
}

const KOLONLAR: Array<{ alan: SiraAlani; ad: string; sag: boolean }> = [
  { alan: 'urunAdi', ad: 'Ürün', sag: false },
  { alan: 'kategori', ad: 'Kategori', sag: false },
  { alan: 'ciro', ad: 'Ciro', sag: true },
  { alan: 'maliyet', ad: 'Maliyet', sag: true },
  { alan: 'kar', ad: 'Kâr', sag: true },
  { alan: 'marj', ad: 'Marj', sag: true },
];

/** En cok satan blogundan acilir: donemde satan butun urunler, kolon basligina basinca siralanir, ada gore aranir. */
export function TumUrunlerDetay({ kaynaklar, donem, sube, kanal, ac }: DetayGovdeProps) {
  const [arama, setArama] = useState('');
  const [sira, setSira] = useState<Siralama>({ alan: 'ciro', azalan: true });
  const [adet, setAdet] = useState(SAYFA_BOYU);
  const q = useSaSorgu<UrunSatiri>(kaynaklar.urunKarliligi, { donem, sube, kanal });
  const veri = q.data?.data;
  const satirlar = useMemo(() => urunSatirlari(veri ?? []), [veri]);
  const suzulu = useMemo(
    () => urunSirala(aramaSuz(satirlar, arama, (u) => [u.urunAdi, u.kategori, u.stokKodu]), sira),
    [satirlar, arama, sira],
  );

  const sirala = (alan: SiraAlani) => {
    // Metin kolonu A'dan Z'ye, sayi kolonu buyukten kucuge baslar; ayni kolona ikinci basis yonu cevirir.
    setSira((s) => (s.alan === alan ? { alan, azalan: !s.azalan } : { alan, azalan: alan !== 'urunAdi' && alan !== 'kategori' }));
    setAdet(SAYFA_BOYU);
  };

  if (!kaynaklar.urunKarliligi)
    return <DetayMesaj testid="tum-urun-kaynak-yok">Ürün raporu bu firmada tanımlı değil.</DetayMesaj>;
  if (q.isLoading) return <DetayYukleniyor testid="tum-urun-yukleniyor" />;
  if (q.isError) return <DetayMesaj testid="tum-urun-hata">Ürünler yüklenemedi.</DetayMesaj>;
  if (satirlar.length === 0) return <DetayMesaj testid="tum-urun-bos">Dönemde satış yok.</DetayMesaj>;

  const gorunen = suzulu.slice(0, adet);
  return (
    <div data-testid="tum-urun" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <AramaKutusu
          testid="tum-urun-ara"
          placeholder="Ürün, kategori ya da kod ara"
          ariaLabel="Ürün ara"
          value={arama}
          onChange={(m) => {
            setArama(m);
            setAdet(SAYFA_BOYU);
          }}
        />
        <span data-testid="tum-urun-sayac" className="text-[12px] text-ink-500 tnum">
          {fmtNum(suzulu.length)} / {fmtNum(satirlar.length)} ürün
        </span>
      </div>
      <p data-testid="tum-urun-kdv-notu" className="text-[12px] text-ink-500">{KDV_KAR_NOTU}</p>
      {suzulu.length === 0 ? (
        <DetayMesaj testid="tum-urun-arama-bos">Aramaya uyan ürün yok.</DetayMesaj>
      ) : (
        <div className="overflow-x-auto">
          <table data-testid="tum-urun-tablo" className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-[12px] text-ink-500 border-b border-border">
                {KOLONLAR.map((k) => {
                  const secili = sira.alan === k.alan;
                  return (
                    <th
                      key={k.alan}
                      aria-sort={secili ? (sira.azalan ? 'descending' : 'ascending') : 'none'}
                      className={`font-medium py-2 px-2 whitespace-nowrap ${k.sag ? 'text-right' : 'text-left'}`}
                    >
                      <button
                        type="button"
                        data-testid={`tum-urun-sirala-${k.alan}`}
                        onClick={() => sirala(k.alan)}
                        className={`focus-ring hover:text-ink-900 ${secili ? 'text-ink-900' : ''}`}
                      >
                        {k.ad}
                        <span aria-hidden="true" className="ml-1 inline-block w-2">
                          {secili ? (sira.azalan ? '↓' : '↑') : ''}
                        </span>
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {gorunen.map((u) => (
                <tr key={u.stokKodu} data-testid={`tum-urun-satir-${u.stokKodu}`} className="border-b border-border last:border-b-0">
                  <td className="py-2 px-2 max-w-0 w-full">
                    <button
                      type="button"
                      data-testid={`tum-urun-detay-${u.stokKodu}`}
                      onClick={() => ac({ tur: 'urun', stokKodu: u.stokKodu, urunAdi: u.urunAdi })}
                      className="focus-ring block w-full truncate text-left text-ink-900 hover:underline underline-offset-2"
                    >
                      {u.urunAdi}
                    </button>
                  </td>
                  <td className="py-2 px-2 text-ink-500 whitespace-nowrap">{u.kategori}</td>
                  <td className="py-2 px-2 text-right tnum text-ink-900 whitespace-nowrap">{fmtTRY(u.ciro)}</td>
                  <td className="py-2 px-2 text-right tnum text-ink-700 whitespace-nowrap">{fmtTRY(u.maliyet)}</td>
                  <td className={`py-2 px-2 text-right tnum whitespace-nowrap ${u.kar < 0 ? 'text-neg-700' : 'text-ink-700'}`}>{fmtTRY(u.kar)}</td>
                  <td className={`py-2 px-2 text-right tnum whitespace-nowrap ${u.marj !== null && u.marj < 0 ? 'text-neg-700' : 'text-ink-500'}`}>
                    {u.marj === null ? '-' : oranMetni(u.marj, 1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {suzulu.length > adet && (
        <button
          type="button"
          data-testid="tum-urun-daha"
          onClick={() => setAdet((a) => a + SAYFA_BOYU)}
          className="focus-ring self-center rounded-md border border-border px-3 py-1.5 text-[12px] text-ink-700 hover:text-ink-900"
        >
          {fmtNum(Math.min(SAYFA_BOYU, suzulu.length - adet))} ürün daha göster
        </button>
      )}
    </div>
  );
}
